/**
 * Topology chorus — sustained harmonic voices for graph membership.
 *
 * Cycles, chain depth, and friend/enemy geometry become a slow-moving
 * chord. Living rechoose adds gentle chorus width; freeze holds a calm
 * stable harmony. Join / rejoin (epoch) glides the chord — never a
 * snap, click, or Geiger tick. Continuous sines only.
 */

import type { SimParams } from "../../sim/params";
import type { AudioVoiceContext, AudioVoicePlugin } from "../../plugins/types";
import type { AudioFeatureFrame } from "../features";
import {
  clamp01,
  intervalHz,
  midiToHz,
  scaleHz,
  softGain,
  TAU,
} from "../music";
import type { AudioParams } from "../params";

const VOICES = 4;

/** Consonant intervals above root (semitones): root, m3, P5, octave. */
const VOICE_SEMIS = [0, 3, 7, 12] as const;

export function mapTopologyBed(
  frame: AudioFeatureFrame,
  audio: AudioParams = { pitchAmount: 0.2 } as never,
): {
  gain: number;
  freqs: number[];
  /** per-voice relative levels (membership richness) */
  levels: number[];
  /** cents of slow chorus width */
  chorusCents: number;
} {
  const top = frame.topology;
  const g = frame.gravity;

  // Structure presence — cycles and short chains = coherent membership
  const cycleT = clamp01(top.nCycles / 10);
  const chainT = clamp01(1 - top.meanChainDepth / 14);
  const structure = cycleT * 0.55 + chainT * 0.3 + clamp01(g.clump) * 0.15;

  // Living graph breathes a little more; freeze is a held calm chord
  const living = top.freeze
    ? 0.12
    : 0.28 + 0.35 * clamp01(top.rechooseProb);
  const gain = softGain(
    0.3 + 0.7 * structure * (0.55 + living),
    top.freeze ? 0.08 : 0.11,
  );

  // Chord root wanders slowly with link geometry:
  // friends close / low tension → lower, warmer; open enemy-heavy → slightly higher
  const friendClose = clamp01(1 - top.meanFriendDist / 1.5);
  const rootT = clamp01(
    0.25 + 0.4 * friendClose + 0.2 * cycleT + 0.15 * (1 - top.linkTension),
  );
  const pitchAmt =
    typeof audio.pitchAmount === "number" ? audio.pitchAmount : 0.2;
  // Continuous scale glide (no floor snap) — ~A3 region above gravity bed
  const rootHz = scaleHz(rootT, 57, 0.9, Math.max(0.12, pitchAmt));

  // Voicing openness from link tension + clump:
  // clumped + friends close → tighter (intervals pull in);
  // tension / open cloud → slightly wider but still consonant.
  const open =
    0.35 * top.linkTension + 0.4 * (1 - clamp01(g.clump)) + 0.25 * (1 - friendClose);

  const freqs: number[] = [];
  for (let v = 0; v < VOICES; v++) {
    const base = VOICE_SEMIS[v]!;
    // Mild stretch of upper voices only — keeps root solid
    const stretch = v === 0 ? 0 : open * (0.35 + 0.25 * v);
    freqs.push(intervalHz(rootHz, base + stretch));
  }

  // Membership richness: more cycles → higher voices fade in
  const levels = [
    0.3,
    0.22,
    0.14 + 0.1 * cycleT,
    0.06 + 0.12 * cycleT * chainT,
  ];

  // Living graph: soft chorus width; frozen: nearly pure unison feel
  // Epoch change adds a tiny extra shimmer (glide handles the pitch)
  const chorusCents = top.freeze
    ? 1.5
    : 4 + 10 * clamp01(top.rechooseProb) + (top.epochChanged ? 3 : 0);

  return { gain, freqs, levels, chorusCents };
}

type Partial = { osc: OscillatorNode; gain: GainNode };
type Nodes = {
  parts: Partial[];
  master: GainNode;
  filter: BiquadFilterNode;
  /** smoothed glide bias on epoch (unitless) */
  chordBias: number;
};

function createTopologyChorus(): AudioVoicePlugin {
  let nodes: Nodes | null = null;
  return {
    id: "topology_grains", // stable id (schema/registry); sound is a chorus now
    label: "Topology chorus",
    tier: "lab",
    enabled(audio: AudioParams, _sim: SimParams) {
      return audio.enabled && audio.topologyVoice;
    },
    start(acx: AudioVoiceContext, _audio: AudioParams) {
      if (nodes) return;
      const { ctx, dest } = acx;
      const master = ctx.createGain();
      master.gain.value = 0;
      const filter = ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.value = 1400;
      filter.Q.value = 0.3;
      filter.connect(master);
      master.connect(dest);

      const parts: Partial[] = [];
      for (let i = 0; i < VOICES; i++) {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = midiToHz(57 + VOICE_SEMIS[i]!);
        const g = ctx.createGain();
        g.gain.value = 0.2 - i * 0.03;
        osc.connect(g);
        g.connect(filter);
        osc.start();
        parts.push({ osc, gain: g });
      }
      nodes = { parts, master, filter, chordBias: 0 };
    },
    process(frame: AudioFeatureFrame, audio: AudioParams, t: number) {
      if (!nodes) return;
      const m = mapTopologyBed(frame, audio);

      // Epoch / rejoin: ease a tiny pitch bias — slow glide, not a transient
      if (frame.topology.epochChanged) {
        nodes.chordBias = Math.min(1, nodes.chordBias + 0.08);
      } else {
        nodes.chordBias *= 0.94;
      }
      const biasRatio = 1 + 0.01 * nodes.chordBias; // ~17 cents max

      nodes.master.gain.setTargetAtTime(m.gain, t, TAU.gain);
      // Warmth follows structure a little
      const cut =
        900 +
        clamp01(frame.topology.nCycles / 10) * 500 +
        (1 - clamp01(frame.gravity.clump)) * 200;
      nodes.filter.frequency.setTargetAtTime(cut, t, TAU.filter);

      for (let i = 0; i < nodes.parts.length; i++) {
        const p = nodes.parts[i]!;
        const f =
          (m.freqs[i] ?? m.freqs[0]!) *
          (i % 2 === 0 ? biasRatio : 1 / biasRatio);
        p.osc.frequency.setTargetAtTime(f, t, TAU.chord);
        // Micro-detune chorus (smooth cents) — living membership shimmer
        const det =
          (i - (VOICES - 1) / 2) * m.chorusCents * (0.55 + 0.35 * (i % 2));
        p.osc.detune.setTargetAtTime(det, t, TAU.pitch);
        const lvl = m.levels[i] ?? 0.15;
        p.gain.gain.setTargetAtTime(lvl, t, TAU.gain);
      }
    },
    stop(t: number) {
      if (!nodes) return;
      try {
        nodes.master.gain.setTargetAtTime(0, t, 0.28);
        for (const p of nodes.parts) p.osc.stop(t + 0.85);
      } catch {
        /* */
      }
      nodes = null;
    },
  };
}

export const topologyGrainsVoice: AudioVoicePlugin = createTopologyChorus();
